import React from 'react';
import { View, Text, SectionList } from 'react-native';
import OrderResumeProps from 'types/components/OrderResumeProps';
import DetailItem, { DetailOrganizedByTomorrowBenefit } from 'types/models/DetailItem';
import { styles } from 'styles/components/orderResume';

export default React.memo(
  ({
    detail,
    showTomorrowBenefit,
    priceWithBenefits,
    translation,
    currentShoppingCartHasAppliedBenefits,
    appColor,
  }: OrderResumeProps): JSX.Element => {
    const organizedDetailByTomorrowBenefits = (currentDetail: DetailItem[]): DetailOrganizedByTomorrowBenefit[] => {
      const organizedDetail: DetailOrganizedByTomorrowBenefit[] = [];
      if (currentDetail && currentDetail.length > 0) {
        detail.forEach((detailItem: DetailItem) => {
          const detailItemHasBenefits = detailItem.tomorrowBenefitQuantity && detailItem.tomorrowBenefitQuantity > 0;
          const findBenefitsSection = organizedDetail.find(
            (organizedDetailItem) => organizedDetailItem.title === translation('tomorrow-with-benefits-title'),
          );
          const findWithoutBenefitsSection = organizedDetail.find(
            (organizedDetailItem) => organizedDetailItem.title === translation('tomorrow-without-benefits-title'),
          );
          if (detailItemHasBenefits) {
            const detailQuantityDifferenceWithBenefit = detailItem.tomorrowBenefitQuantity
              ? detailItem.quantity - detailItem.tomorrowBenefitQuantity
              : 0;
            if (detailQuantityDifferenceWithBenefit > 0) {
              const detailWithBenefit = Object.assign({}, detailItem);
              if (detailItem.tomorrowBenefitQuantity) {
                detailWithBenefit.quantity = detailItem.tomorrowBenefitQuantity;
              }
              if (findBenefitsSection) {
                findBenefitsSection.data.push(detailWithBenefit);
              } else {
                organizedDetail.push({
                  title: translation('tomorrow-with-benefits-title'),
                  data: [detailWithBenefit],
                });
              }
              const detailWithoutBenefit = Object.assign({}, detailItem);
              detailWithoutBenefit.quantity = detailQuantityDifferenceWithBenefit;
              detailWithoutBenefit.tomorrowBenefitQuantity = undefined;
              if (findWithoutBenefitsSection) {
                findWithoutBenefitsSection.data.push(detailWithoutBenefit);
              } else {
                organizedDetail.push({
                  title: translation('tomorrow-without-benefits-title'),
                  data: [detailWithoutBenefit],
                });
              }
            } else {
              if (findBenefitsSection) {
                findBenefitsSection.data.push(detailItem);
              } else {
                organizedDetail.push({
                  title: translation('tomorrow-with-benefits-title'),
                  data: [detailItem],
                });
              }
            }
          } else {
            if (findWithoutBenefitsSection) {
              findWithoutBenefitsSection.data.push(detailItem);
            } else {
              organizedDetail.push({
                title: translation('tomorrow-without-benefits-title'),
                data: [detailItem],
              });
            }
          }
        });
      }
      return organizedDetail;
    };

    const RenderDetailItem = ({ item }: { item: DetailItem }): JSX.Element => {
      const containsTomorrowBenefit = showTomorrowBenefit(item, true);
      return (
        <View style={[styles.summaryRow, { marginTop: 10 }]}>
          <View style={{ flex: 1 }}>
            <Text
              style={{
                paddingLeft: 7,
              }}
              numberOfLines={2}
              ellipsizeMode={'tail'}
            >
              {`${item.product.name} ${
                item.tomorrowBenefitQuantity ? `(${item.tomorrowBenefitQuantity})` : `(${item.quantity})`
              }`}
            </Text>
          </View>

          <View style={{ flex: 0.4 }}>
            <Text style={[styles.orderResumeItemDetailText, containsTomorrowBenefit && styles.oldPriceText]}>
              {`${translation('amount', {
                amount: Math.round(item.product.price * item.quantity * 100) / 100,
              })}`}
            </Text>
            {containsTomorrowBenefit && (
              <Text style={[styles.orderResumeItemDetailText, styles.discountedPriceText, { color: appColor }]}>
                {`${translation('amount', { amount: priceWithBenefits(item) })}`}
              </Text>
            )}
          </View>
        </View>
      );
    };

    return (
      <>
        {currentShoppingCartHasAppliedBenefits ? (
          <SectionList
            sections={organizedDetailByTomorrowBenefits(detail)}
            keyExtractor={(item, index) => item.product.id + index}
            renderSectionHeader={({ section: { title } }) => (
              <Text
                style={{
                  marginTop: 12,
                  fontStyle: 'normal',
                  fontWeight: '500',
                  fontSize: 14,
                  lineHeight: 14,
                }}
              >
                {title}
              </Text>
            )}
            renderItem={({ item, index }) => <RenderDetailItem item={item} key={index} />}
          />
        ) : (
          detail.map((item, index) => <RenderDetailItem item={item} key={index} />)
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.detail.length !== nextProps.detail.length) {
      return false;
    }
    prevProps.detail.forEach((_value, index, arr) => {
      if ('product' in arr[index] && arr[index].product.id !== nextProps.detail[index].product.id) {
        return false;
      }

      if (
        'product' in arr[index] &&
        arr[index].product.id === nextProps.detail[index].product.id &&
        arr[index].quantity !== nextProps.detail[index].quantity
      ) {
        return false;
      }
    });
    return (
      prevProps.showTomorrowBenefit === nextProps.showTomorrowBenefit &&
      prevProps.priceWithBenefits === nextProps.priceWithBenefits &&
      prevProps.translation === nextProps.translation &&
      prevProps.currentShoppingCartHasAppliedBenefits === nextProps.currentShoppingCartHasAppliedBenefits &&
      prevProps.appColor === nextProps.appColor
    );
  },
);
